import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionProposal } from '../../../lib/api/actions/action-types';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import '../actions.scss';

interface Props {
  proposal: ActionProposal;
  onApprove?: (id: string, notes?: string) => void;
  onDeny?: (id: string, reason?: string) => void;
  showActions: boolean;
}

const ActionProposalCard = ({
  proposal,
  onApprove,
  onDeny,
  showActions,
}: Props) => {
  const { t } = useTranslation();
  const [showApproveInput, setShowApproveInput] = useState(false);
  const [showDenyInput, setShowDenyInput] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [denyReason, setDenyReason] = useState('');

  const getPlatformLabel = (platform: string): string => {
    switch (platform) {
      case 'facebook_ads':
        return 'FB';
      case 'google_ads':
        return 'GA';
      case 'tiktok_ads':
        return 'TT';
      case 'linkedin_ads':
        return 'LI';
      default:
        return platform.substring(0, 2).toUpperCase();
    }
  };

  const getPlatformName = (platform: string): string => {
    switch (platform) {
      case 'facebook_ads':
        return 'Facebook Ads';
      case 'google_ads':
        return 'Google Ads';
      case 'tiktok_ads':
        return 'TikTok Ads';
      case 'linkedin_ads':
        return 'LinkedIn Ads';
      default:
        return platform;
    }
  };

  const getActionTypeLabel = (actionType?: ActionProposal['action_type']): string => {
    if (!actionType) return t('Action');
    const name = actionType.name || actionType.action_key || '';
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatValue = (value: Record<string, any>): string => {
    if (!value) return '-';
    // Try common value fields
    if (value.daily_budget !== undefined) {
      return `$${(Number(value.daily_budget) / 100).toFixed(2)}/day`;
    }
    if (value.lifetime_budget !== undefined) {
      return `$${(Number(value.lifetime_budget) / 100).toFixed(2)} lifetime`;
    }
    if (value.bid_amount !== undefined) {
      return `$${(Number(value.bid_amount) / 100).toFixed(2)} bid`;
    }
    if (value.status !== undefined) {
      return String(value.status);
    }
    // Fallback: show first key-value
    const keys = Object.keys(value);
    if (keys.length === 0) return '-';
    const firstKey = keys[0];
    return `${firstKey}: ${value[firstKey]}`;
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('JustNow');
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleApprove = () => {
    if (showApproveInput) {
      onApprove?.(proposal.id, approveNotes || undefined);
      setShowApproveInput(false);
      setApproveNotes('');
    } else {
      setShowApproveInput(true);
      setShowDenyInput(false);
    }
  };

  const handleDeny = () => {
    if (showDenyInput) {
      onDeny?.(proposal.id, denyReason || undefined);
      setShowDenyInput(false);
      setDenyReason('');
    } else {
      setShowDenyInput(true);
      setShowApproveInput(false);
    }
  };

  const renderResultInfo = () => {
    if (proposal.status === 'completed' && proposal.execution_result) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--success'>
          <span className='action-proposal-card__result-label'>
            {t('Result')}:
          </span>
          {typeof proposal.execution_result === 'object'
            ? JSON.stringify(proposal.execution_result)
            : String(proposal.execution_result)}
        </div>
      );
    }

    if (proposal.status === 'failed' && proposal.execution_error) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--error'>
          <span className='action-proposal-card__result-label'>
            {t('Error')}:
          </span>
          {proposal.execution_error}
        </div>
      );
    }

    if (proposal.status === 'denied' && proposal.review_notes) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--denied'>
          <span className='action-proposal-card__result-label'>
            {t('DenialReason')}:
          </span>
          {proposal.review_notes}
        </div>
      );
    }

    return null;
  };

  return (
    <div className='action-proposal-card'>
      {/* Header */}
      <div className='action-proposal-card__header'>
        <div className='action-proposal-card__header-left'>
          <div className='action-proposal-card__header-platform'>
            {getPlatformLabel(proposal.platform)}
          </div>
          <h4 className='action-proposal-card__header-title'>
            {getActionTypeLabel(proposal.action_type)}
          </h4>
        </div>
        <span className={`risk-badge risk-badge--${proposal.risk_level}`}>
          {proposal.risk_level.toUpperCase()}
        </span>
      </div>

      {/* Entity info */}
      <p className='action-proposal-card__entity'>
        {proposal.entity_name || proposal.entity_id}
        {proposal.entity_name && ` (ID: ${proposal.entity_id})`}
        {' - '}
        {getPlatformName(proposal.platform)}
      </p>

      <hr className='action-proposal-card__divider' />

      {/* Value change */}
      <div className='value-change'>
        <span className='value-change__label'>{t('Current')}:</span>
        <span className='value-change__current'>
          {formatValue(proposal.current_value)}
        </span>
        <span className='value-change__arrow'>&rarr;</span>
        <span className='value-change__label'>{t('Proposed')}:</span>
        <span className='value-change__proposed'>
          {formatValue(proposal.proposed_value)}
        </span>
        {proposal.change_percentage !== null && (
          <span
            className={`value-change__percentage ${
              proposal.change_percentage > 0
                ? 'value-change__percentage--positive'
                : proposal.change_percentage < 0
                ? 'value-change__percentage--negative'
                : 'value-change__percentage--neutral'
            }`}
          >
            {proposal.change_percentage > 0 ? '+' : ''}
            {proposal.change_percentage.toFixed(1)}%
          </span>
        )}
      </div>

      <hr className='action-proposal-card__divider' />

      {/* Reasoning */}
      <div className='action-proposal-card__reasoning'>
        <p className='action-proposal-card__reasoning-label'>
          {t('Reasoning')}
        </p>
        <p className='action-proposal-card__reasoning-text'>
          {proposal.reasoning}
        </p>
      </div>

      {/* Result info for history view */}
      {renderResultInfo()}

      {/* Footer */}
      <div className='action-proposal-card__footer'>
        <div className='action-proposal-card__footer-meta'>
          <span className={`status-badge status-badge--${proposal.status}`}>
            {proposal.status.replace(/_/g, ' ')}
          </span>
          <span>{getRelativeTime(proposal.created_at)}</span>
          {proposal.auto_approved && (
            <span className='platform-tag'>{t('AutoApproved')}</span>
          )}
          {proposal.rule_name && (
            <span className='platform-tag'>
              {t('Rule')}: {proposal.rule_name}
            </span>
          )}
        </div>

        {showActions &&
          (proposal.status === 'pending_review' ||
            proposal.status === 'proposed') && (
            <div className='action-proposal-card__footer-actions'>
              <MainSquareButton
                value={showApproveInput ? t('Confirm') : t('Approve')}
                type='button'
                buttonType='success'
                onClick={handleApprove}
              />
              <MainSquareButton
                value={showDenyInput ? t('Confirm') : t('Deny')}
                type='button'
                buttonType='delete'
                onClick={handleDeny}
              />
            </div>
          )}
      </div>

      {/* Inline approval notes input */}
      {showApproveInput && (
        <div className='action-proposal-card__inline-input'>
          <input
            type='text'
            placeholder={t('OptionalApprovalNotes')}
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApprove();
            }}
          />
          <MainSquareButton
            value={t('Cancel')}
            type='button'
            buttonType='white'
            onClick={() => {
              setShowApproveInput(false);
              setApproveNotes('');
            }}
          />
        </div>
      )}

      {/* Inline denial reason input */}
      {showDenyInput && (
        <div className='action-proposal-card__inline-input'>
          <input
            type='text'
            placeholder={t('OptionalDenialReason')}
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDeny();
            }}
          />
          <MainSquareButton
            value={t('Cancel')}
            type='button'
            buttonType='white'
            onClick={() => {
              setShowDenyInput(false);
              setDenyReason('');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ActionProposalCard;
